import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { AlertTriangle, Check, X, Sparkles, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { addFurnitureItem } from '@/lib/api';

// Normalize item names for fuzzy matching.
const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Find candidate items in a project whose name matches the target line item.
function findCandidates(project, targetName) {
  const target = norm(targetName);
  if (!target) return [];
  const out = [];
  for (const room of (project.rooms || [])) {
    for (const it of (room.items || [])) {
      const n = norm(it.name);
      let score = 0;
      if (n === target) score = 100;
      else if (n && (n.includes(target) || target.includes(n))) score = 60;
      // Word overlap fallback
      else {
        const tw = new Set(target.split(' ').filter(Boolean));
        const iw = new Set(n.split(' ').filter(Boolean));
        let hits = 0;
        for (const w of tw) if (iw.has(w)) hits += 1;
        if (hits > 0) score = Math.round((hits / Math.max(tw.size, 1)) * 40);
      }
      if (score > 0) {
        out.push({
          score,
          room_id: room.id, room_name: room.name,
          item_id: it.id, item_name: it.name,
        });
      }
    }
  }
  out.sort((a, b) => b.score - a.score);
  return out;
}

/**
 * Smart-allocate dialog for a single receipt line + a master project.
 *
 * For each selected sub-project of the master, pre-picks the best matching
 * item by name. The user can adjust each pick (or skip), and apply.
 *
 * Resolution modes for sub-projects with no match:
 *   - skip: nothing is allocated to that sub-project
 *   - pick: user manually selects a room/item from a dropdown
 *   - create: a new item is created in a chosen room (matches the line name)
 *
 * Quantity-mismatch banner shows up if line qty < selectedSubs.length, and
 * the user can choose which N sub-projects to target.
 */
export function MasterAllocateDialog({
  open, onOpenChange, line, master, selectedSubs, onApply,
}) {
  // selections: { [sub_project_id]: { mode, room_id, item_id, include } }
  const [selections, setSelections] = useState({});
  const [busy, setBusy] = useState(false);

  // Reset on open
  useEffect(() => {
    if (!open || !line) return;
    const init = {};
    for (const sub of selectedSubs) {
      const candidates = findCandidates(sub, line.name);
      const top = candidates[0];
      init[sub.id] = top
        ? { include: true, mode: 'pick', room_id: top.room_id, item_id: top.item_id }
        : { include: true, mode: 'create', room_id: (sub.rooms?.[0]?.id || ''), item_id: '' };
    }
    setSelections(init);
  }, [open, line?.id, master?.id, selectedSubs.length]);

  const qty = Math.max(0, Math.round(Number(line?.quantity) || 0));
  const subCount = selectedSubs.length;
  const includedCount = Object.values(selections).filter(s => s.include && (s.mode !== 'skip')).length;
  const qtyShort = qty > 0 && qty < subCount;

  const setSel = (subId, patch) => {
    setSelections(prev => ({ ...prev, [subId]: { ...prev[subId], ...patch } }));
  };

  const setAllInclude = (val) => {
    setSelections(prev => {
      const next = { ...prev };
      for (const k of Object.keys(next)) next[k] = { ...next[k], include: val };
      return next;
    });
  };

  const trimToFirstN = (n) => {
    setSelections(prev => {
      const next = { ...prev };
      let kept = 0;
      for (const sub of selectedSubs) {
        const s = next[sub.id] || {};
        if (kept < n && (s.mode !== 'skip')) {
          next[sub.id] = { ...s, include: true };
          kept += 1;
        } else {
          next[sub.id] = { ...s, include: false };
        }
      }
      return next;
    });
  };

  const applyAll = async () => {
    setBusy(true);
    try {
      const ops = []; // { project_id, room_id, item_id, count, sub }
      for (const sub of selectedSubs) {
        const s = selections[sub.id];
        if (!s || !s.include) continue;
        if (s.mode === 'skip') continue;
        let room_id = s.room_id;
        let item_id = s.item_id;
        if (s.mode === 'create') {
          if (!room_id) {
            toast.error(`Pick a room to create the item in for ${sub.name}`);
            setBusy(false); return;
          }
          // Create the missing item on the fly.
          try {
            const created = await addFurnitureItem(sub.id, room_id, {
              name: line.name,
              budget: line.unit_price || null,
            });
            item_id = created.id;
          } catch (e) {
            toast.error(`Failed to create item in ${sub.name}`);
            setBusy(false); return;
          }
        }
        if (!item_id) continue;
        ops.push({ project_id: sub.id, room_id, item_id, count: 1, sub_name: sub.name });
      }
      onApply?.(ops);
      toast.success(`Allocated 1 unit to ${ops.length} sub-project${ops.length === 1 ? '' : 's'}`);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  if (!line || !master) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0" data-testid="master-allocate-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#2d948c]" />
            Allocate <span className="text-[#2d948c]">&quot;{line.name}&quot;</span> across &quot;{master.name}&quot;
          </DialogTitle>
          <DialogDescription>
            One unit is allocated to each selected sub-project. Adjust the target item per unit below.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-3 space-y-3">
          {qtyShort && (
            <Card className="border-amber-300 bg-amber-50/60 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <div className="text-xs space-y-1.5 flex-1">
                  <p className="font-semibold text-amber-900">
                    Quantity mismatch: receipt has {qty} unit{qty === 1 ? '' : 's'} but {subCount} sub-projects are selected.
                  </p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1"
                      onClick={() => trimToFirstN(qty)}
                      data-testid="master-allocate-trim-btn"
                    >
                      Allocate only the first {qty}
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]"
                      onClick={() => setAllInclude(true)}
                      data-testid="master-allocate-include-all"
                    >
                      Include all (over-allocate)
                    </Button>
                  </div>
                  <p className="text-[10px] text-amber-800/80 pt-1">
                    The remaining sub-projects can be allocated from a follow-up receipt later.
                  </p>
                </div>
              </div>
            </Card>
          )}

          <div className="flex items-center justify-between">
            <p className="text-[11px] text-[#5b6773]">
              <span className="font-semibold tabular-nums">{includedCount}</span> of {subCount} sub-projects will receive 1 unit each.
            </p>
            <div className="flex gap-1.5">
              <button onClick={() => setAllInclude(true)} className="text-[10px] text-[#2d948c] hover:underline" data-testid="master-allocate-all">All</button>
              <span className="text-[#5b6773]">·</span>
              <button onClick={() => setAllInclude(false)} className="text-[10px] text-[#5b6773] hover:underline" data-testid="master-allocate-none">None</button>
            </div>
          </div>

          <div className="space-y-2">
            {selectedSubs.map(sub => {
              const s = selections[sub.id] || { include: true, mode: 'skip' };
              const candidates = findCandidates(sub, line.name);
              const hasMatch = candidates.length > 0;
              return (
                <Card
                  key={sub.id}
                  className={`p-3 ${!s.include ? 'opacity-50' : ''}`}
                  data-testid={`master-allocate-sub-${sub.id}`}
                >
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSel(sub.id, { include: !s.include })}
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        s.include
                          ? 'bg-[#2d948c] border-[#2d948c] text-white'
                          : 'border-[#5b6773]/30 text-transparent hover:border-[#2d948c]'
                      }`}
                      data-testid={`master-allocate-sub-toggle-${sub.id}`}
                    >
                      <Check className="h-3 w-3" />
                    </button>
                    <p className="text-sm font-medium text-[#1f2933] flex-1 min-w-0 truncate">{sub.name}</p>
                    {!hasMatch && (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                        No match
                      </span>
                    )}
                  </div>

                  {s.include && (
                    <div className="mt-2 pl-7 space-y-2">
                      {/* Mode picker */}
                      <div className="inline-flex text-[10px] rounded-full border border-[#2d948c]/25 p-0.5 bg-white">
                        <button
                          type="button"
                          onClick={() => setSel(sub.id, { mode: 'pick' })}
                          disabled={(sub.rooms || []).every(r => !(r.items || []).length)}
                          className={`px-2 py-0.5 rounded-full transition-colors ${s.mode === 'pick' ? 'bg-[#2d948c] text-white' : 'text-[#5b6773] disabled:opacity-40'}`}
                          data-testid={`master-allocate-mode-pick-${sub.id}`}
                        >
                          Pick existing item
                        </button>
                        <button
                          type="button"
                          onClick={() => setSel(sub.id, { mode: 'create', item_id: '', room_id: s.room_id || sub.rooms?.[0]?.id || '' })}
                          className={`px-2 py-0.5 rounded-full transition-colors ${s.mode === 'create' ? 'bg-[#2d948c] text-white' : 'text-[#5b6773]'}`}
                          data-testid={`master-allocate-mode-create-${sub.id}`}
                        >
                          Create new item
                        </button>
                        <button
                          type="button"
                          onClick={() => setSel(sub.id, { mode: 'skip' })}
                          className={`px-2 py-0.5 rounded-full transition-colors ${s.mode === 'skip' ? 'bg-[#2d948c] text-white' : 'text-[#5b6773]'}`}
                          data-testid={`master-allocate-mode-skip-${sub.id}`}
                        >
                          Skip
                        </button>
                      </div>

                      {/* Pick existing item */}
                      {s.mode === 'pick' && (
                        <Select
                          value={s.item_id || ''}
                          onValueChange={(v) => {
                            // value is "roomId::itemId"
                            const [rid, iid] = v.split('::');
                            setSel(sub.id, { room_id: rid, item_id: iid });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`master-allocate-pick-${sub.id}`}>
                            <SelectValue placeholder="Choose an item…" />
                          </SelectTrigger>
                          <SelectContent>
                            {(sub.rooms || []).flatMap(r =>
                              (r.items || []).map(it => (
                                <SelectItem key={`${r.id}::${it.id}`} value={`${r.id}::${it.id}`}>
                                  <span className="text-[#5b6773] text-[10px]">{r.name} ·</span> {it.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      )}

                      {/* Create new item */}
                      {s.mode === 'create' && (
                        <div className="space-y-1.5">
                          <Select
                            value={s.room_id || ''}
                            onValueChange={(v) => setSel(sub.id, { room_id: v })}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`master-allocate-create-room-${sub.id}`}>
                              <SelectValue placeholder="Choose a room to create the item in…" />
                            </SelectTrigger>
                            <SelectContent>
                              {(sub.rooms || []).map(r => (
                                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-[#5b6773]">
                            A new item named &quot;{line.name}&quot; will be created in the chosen room.
                          </p>
                        </div>
                      )}

                      {s.mode === 'skip' && (
                        <p className="text-[11px] text-[#5b6773] italic">No allocation for this unit.</p>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
            {selectedSubs.length === 0 && (
              <p className="text-sm text-[#5b6773] py-6 text-center italic">
                No sub-projects of {master.name} are currently selected at the top of the page.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={applyAll}
            disabled={busy || includedCount === 0}
            className="gap-1.5"
            data-testid="master-allocate-apply"
          >
            <Plus className="h-3.5 w-3.5" />
            Allocate to {includedCount} unit{includedCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
